import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CircleDollarSign,
  Cpu,
  Gauge,
  Globe2,
  Timer,
} from "lucide-react";

import { api } from "@/api/client";
import { usePoolPlacements } from "@/api/local/pools";
import type {
  CostTimeseriesResult,
  Pool,
  Replica,
  ResolvedPlacement,
} from "@/api/types";
import { HealthBar } from "@/components/HealthBar";
import { MetricTile, type Tone } from "@/components/MetricTile";
import { MiniGauge } from "@/components/MiniGauge";
import { PoolPhaseCard } from "@/components/PhaseCard";
import { Pill } from "@/components/Pill";
import { Section } from "@/components/Section";
import { Sparkline } from "@/components/Sparkline";
import { humanCloudLabel } from "@/workloads/tree-data";

interface Props {
  pool: Pool;
  replicas: Replica[];
}

/**
 * Pool dashboard, summary only — events live on the global Events tab,
 * pool inventory lives in the left tree. Anything actionable goes in
 * the dedicated subnav tabs.
 */
// Statuses in which a replica is expected to have an open cost-ledger
// interval. Kept in step with the same list in workloads/tree-data.ts
// so the dashboard tile and the tree column agree about what "unknown
// rate" means.
const BILLABLE_STATUSES: Replica["status"][] = [
  "ready",
  "draining",
  "draining_long_lived",
  "shutting_down",
];

export function PoolDashboard({ pool, replicas }: Props) {
  const ts = useQuery({
    queryKey: ["cost-ts", "pool", pool.id],
    queryFn: () =>
      api.get<CostTimeseriesResult>(
        `/cost/timeseries?pool_id=${pool.id}&buckets=24&bucket_seconds=3600`,
      ),
  });

  const stats = useMemo(() => {
    const ready = replicas.filter((r) => r.status === "ready").length;
    const pending = replicas.filter(
      (r) => r.status === "pending" || r.status === "provisioning",
    ).length;
    const failed = replicas.filter(
      (r) => r.status === "failed" || r.status === "not_ready",
    ).length;
    const shutting = replicas.filter(
      (r) => r.status === "shutting_down",
    ).length;
    return {
      ready,
      pending,
      failed,
      shutting,
      total: replicas.length,
      // "Live" excludes `shutting_down` rows: SkyServe's `down` is
      // already in flight, so they no longer hold a slot against the
      // cap. The drain stages (`draining*`) still count because they
      // continue to serve traffic and consume capacity.
      live: replicas.length - shutting,
    };
  }, [replicas]);

  // Numerator = live replicas (everything the controller is tracking
  // *except* rows already shutting down). Denominator = the configured
  // cap. Reads `live/cap` so a ROLLING update overshoot still shows up
  // as e.g. `4/2` (old `draining*` + new ready/provisioning), while a
  // routine drain doesn't get bogus "+N over cap" labelling for replicas
  // SkyServe is in the middle of removing.
  const cap = pool.max_replicas;
  const overCap = cap > 0 && stats.live > cap;
  const baseTone: Tone =
    stats.failed > 0
      ? "danger"
      : stats.total === 0
        ? "neutral"
        : stats.ready === stats.total
          ? "ok"
          : "warn";
  // Don't downgrade danger; otherwise warn for the surge state.
  const tone: Tone = overCap && baseTone !== "danger" ? "warn" : baseTone;

  const avgStartup = pool.avg_startup_seconds ?? null;
  const startupLabel =
    avgStartup == null
      ? "—"
      : avgStartup >= 3600
        ? `${(avgStartup / 3600).toFixed(1)}h`
        : avgStartup >= 60
          ? `${Math.floor(avgStartup / 60)}m ${Math.round(avgStartup % 60)}s`
          : `${Math.round(avgStartup)}s`;

  // Measured, not derived from the cap. This tile used to show
  // `ready * max_price_per_hour`, which is the operator's *budget
  // ceiling* multiplied by a replica count — a number that could
  // disagree with the tree's measured $/HOUR by 5-10x on RunPod and
  // moved whenever someone edited the cap. Both surfaces now read the
  // same open cost-ledger intervals.
  //
  // `null` (rather than 0) when a billable replica has no resolved
  // rate: unknown is not free, and a gauge reading $0.00 against a
  // cap would say the pool is idle when it isn't.
  const liveCostPerHour = useMemo(() => {
    let total = 0;
    let unknown = false;
    for (const r of replicas) {
      const v = r.current_hourly_cost_usd;
      if (typeof v === "number") total += v;
      else if (BILLABLE_STATUSES.includes(r.status)) unknown = true;
    }
    return unknown ? null : total;
  }, [replicas]);
  const capCostPerHour = pool.max_replicas * pool.max_price_per_hour;

  return (
    <>
      <PoolPhaseCard pool={pool} />
      <div className="metric-grid">
        <MetricTile
          icon={<Cpu />}
          label="Replicas"
          value={
            cap > 0
              ? `${stats.live}/${cap}`
              : `${stats.ready}/${stats.live}`
          }
          unit={
            overCap
              ? `+${stats.live - cap} over cap`
              : stats.shutting > 0
                ? `${stats.shutting} shutting down`
                : cap > 0
                  ? `${stats.ready} ready`
                  : "cap 0"
          }
          tone={tone}
          hint={
            <HealthBar
              ready={stats.ready}
              pending={stats.pending}
              failed={stats.failed}
              total={Math.max(stats.live, pool.max_replicas)}
              width={140}
            />
          }
          rows={[
            { label: "ready", value: stats.ready },
            { label: "prov.", value: stats.pending },
            { label: "failed", value: stats.failed },
          ]}
        />
        <MetricTile
          icon={<Timer />}
          label="Avg startup"
          value={startupLabel}
          unit={
            (pool.startup_samples ?? 0) > 0
              ? `avg of ${pool.startup_samples}`
              : "no READY yet"
          }
          hint="launch → READY (capacity hunt + image pull + model load included)"
        />
        <MetricTile
          icon={<CircleDollarSign />}
          label="Cost · 24h"
          value={`$${(ts.data?.total_usd ?? 0).toFixed(2)}`}
          tone={(ts.data?.total_usd ?? 0) > 0 ? "brand" : "neutral"}
          hint={
            <Sparkline
              values={(ts.data?.points ?? []).map((p) => p.cost_usd)}
              width={140}
              height={28}
              tone="brand"
              fill
            />
          }
        />
        <MetricTile
          icon={<Gauge />}
          label="$/h utilisation"
          value={liveCostPerHour == null ? "—" : `$${liveCostPerHour.toFixed(2)}`}
          unit={`/ $${capCostPerHour.toFixed(2)}`}
          hint={
            <MiniGauge
              value={liveCostPerHour ?? 0}
              limit={capCostPerHour}
              width={140}
            />
          }
          rows={[
            { label: "priority", value: `P${pool.priority}` },
            { label: "shape", value: poolShape(pool) },
          ]}
        />
      </div>

      <PlacementBreakdown pool={pool} replicas={replicas} />
    </>
  );
}

/**
 * Per-placement summary table.
 *
 * Lists the resolved `(cloud, region)` candidates (derived from the
 * Provider config inheritance chain) alongside how many live replicas
 * SkyPilot currently has on each. We match a replica against a
 * placement by (case-insensitive) region prefix — SkyPilot tends to
 * report the actual zone (e.g. `us-east-1a`) for a placement whose
 * region we configured as `us-east-1`. Replicas whose region doesn't
 * match any resolved placement land in an "unmatched" row (typically
 * because the operator just disabled that region on the Providers
 * tab, or the cloud's catalog changed).
 */
function PlacementBreakdown({
  pool,
  replicas,
}: {
  pool: Pool;
  replicas: Replica[];
}) {
  const placements = usePoolPlacements(pool.id);
  if (placements.length === 0) return null;

  // Greedy assignment. Region-pinned placements claim replicas whose
  // reported region matches by prefix first; wildcard (`region=null`)
  // placements absorb whatever is left. This avoids double-counting
  // when both a region-pinned and a wildcard placement exist on the
  // same Pool, and reserves the "unmatched" bucket for genuinely
  // orphaned rows.
  const rows: Array<{ placement: ResolvedPlacement; matched: Replica[] }> =
    placements.map((p) => ({ placement: p, matched: [] as Replica[] }));
  const claimed = new Set<number>();
  for (const row of rows) {
    if (row.placement.region == null) continue;
    for (const r of replicas) {
      if (claimed.has(r.id)) continue;
      if (matchesPlacement(r, row.placement)) {
        row.matched.push(r);
        claimed.add(r.id);
      }
    }
  }
  // Pass 2 — wildcards split the residual evenly across themselves
  // (in placement order) so the table doesn't pile every wildcard
  // replica onto a single row when a Pool has multiple wildcards.
  const wildcardRows = rows.filter((row) => row.placement.region == null);
  const residuals = replicas.filter((r) => !claimed.has(r.id));
  for (const r of residuals) {
    if (wildcardRows.length === 0) break;
    const slot = wildcardRows[r.id % wildcardRows.length];
    slot.matched.push(r);
    claimed.add(r.id);
  }
  const unmatched = replicas.filter((r) => !claimed.has(r.id));

  return (
    <Section
      title="Placements"
      icon={<Globe2 />}
      count={`${placements.length}`}
      flush
    >
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Cloud</th>
              <th>Region</th>
              <th style={{ textAlign: "right" }}>Live</th>
              <th style={{ textAlign: "right" }}>Ready</th>
              <th>Status mix</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const ready = row.matched.filter(
                (r) => r.status === "ready",
              ).length;
              return (
                <tr key={`${row.placement.source_cloud}:${row.placement.region ?? ""}:${idx}`}>
                  <td>{humanCloudLabel(row.placement.source_cloud)}</td>
                  <td className="mono">
                    {row.placement.region ?? (
                      <span className="muted">any</span>
                    )}
                  </td>
                  <td className="num">{row.matched.length}</td>
                  <td className="num">{ready}</td>
                  <td>
                    <PlacementStatusMix replicas={row.matched} />
                  </td>
                </tr>
              );
            })}
            {unmatched.length > 0 && (
              <tr>
                <td colSpan={2} className="muted">
                  unmatched (provider region disabled?)
                </td>
                <td className="num">{unmatched.length}</td>
                <td className="num">
                  {unmatched.filter((r) => r.status === "ready").length}
                </td>
                <td>
                  <PlacementStatusMix replicas={unmatched} />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function PlacementStatusMix({ replicas }: { replicas: Replica[] }) {
  if (replicas.length === 0) {
    return <span className="muted">—</span>;
  }
  const ready = replicas.filter((r) => r.status === "ready").length;
  const pending = replicas.filter(
    (r) => r.status === "pending" || r.status === "provisioning",
  ).length;
  const failed = replicas.filter(
    (r) => r.status === "failed" || r.status === "not_ready",
  ).length;
  return (
    <span style={{ display: "inline-flex", gap: 4 }}>
      {ready > 0 && <Pill tone="ok">{ready} ready</Pill>}
      {pending > 0 && <Pill tone="warn">{pending} prov.</Pill>}
      {failed > 0 && <Pill tone="danger">{failed} fail</Pill>}
    </span>
  );
}

function matchesPlacement(replica: Replica, placement: ResolvedPlacement): boolean {
  // Only used for region-pinned placements (the wildcard pass goes
  // through a separate residual loop). SkyPilot frequently reports
  // the actual zone (e.g. `us-east-1a`) for a placement whose region
  // we configured as `us-east-1`, so we match by case-insensitive
  // prefix.
  if (placement.region == null) return false;
  if (!replica.region) return false;
  return replica.region.toLowerCase().startsWith(placement.region.toLowerCase());
}

/** One-line summary of a pool's resource shape for the Tier tile hint. */
function poolShape(pool: Pool): string {
  if (pool.accelerators) return pool.accelerators;
  if (pool.cpus) {
    return `cpu ${pool.cpus}${pool.memory_gb ? ` / ${pool.memory_gb} GiB` : ""}`;
  }
  return "cloud default";
}
